import { useEffect, useRef, useState } from "react";
import PageContainer from "@components/layout/PageContainer";
import { useNavigate, useParams } from "react-router-dom";
import ModelIndex, { ModelIndexHandle } from "@components/layout/ModelIndex";
import { faEdit, faPaperPlane, faTrash } from "@fortawesome/free-solid-svg-icons";
import { ExpenseNoteApi, ExpenseNoteDataType } from "@/api/ExpenseNoteApi";
import { formatMoneyValue } from "@utils/Currency";
import { showAlert } from "@utils/Alert";
import styles from "./style.module.scss";
import { PROVIDER_NOTE_TYPES } from "@utils/Constants";
import { ProviderInvoiceApi, ProviderInvoiceDataType } from "@/api/ProviderInvoiceApi";
import { Col, Container, Row } from "reactstrap";
import { ProviderInvoiceFormDataKeys } from "@/generated/ProviderInvoiceFormDataKeys";
import { ProviderInvoiceRelatedAttributes } from "@/formTypes/ProviderInvoiceDataType";
import { formatDate } from "@utils/Api";
import LoadingScreen from "@components/content/LoadingScreen";
import { FetchResponse } from "@/api/ApiClient";
import { ExpenseApi, ExpenseDataType } from "@/api/ExpenseApi";
import { ExpenseFormDataKeys } from "@/generated/ExpenseFormDataKeys";
import { ExpenseRelatedAttributes } from "@/formTypes/ExpenseDataType";

const ExpenseNoteIndex = () => {
  const navigate = useNavigate();
  const params = useParams();
  const modelRef = useRef<ModelIndexHandle>(null);
  const { expenseId } = params;
  const [expense, setExpense] = useState<ExpenseDataType>({})
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (expenseId) {
      const run = async (expenseId: string) => {
        try {
          const expenseData = await ExpenseApi.get(expenseId, {
            selectionSet: [
              ...ExpenseFormDataKeys.filter(k => !ExpenseRelatedAttributes.includes(k)),
              "currency.*",
              "expenseNotes.*",
              "expenseNotes.currency.*",
            ]
          });
          setExpense(expenseData);
          setTimeout(() => {
            modelRef.current?.refetch()
          });
        } finally {
          setIsLoading(false)
        }
      }
      run(expenseId);
      setIsLoading(true)
    }
  }, [expenseId]);

  const deleteRecord = async (id: string) => {
    const { errors } = await ExpenseNoteApi.delete(id);
    if (errors?.length) {
      showAlert('danger', 'La Nota de Credito/Debito no puedo ser eliminado');
    } else {
      showAlert('success', 'La Nota de Credito/Debito fue eliminado correctamente');
    }
    modelRef.current?.refetch();
  }

  const getInvoiceNotes = async (): Promise<FetchResponse<ExpenseNoteDataType>> => {
    return {
      items: expense.expenseNotes || []
    }
  }

  return (
    <PageContainer
      title="Lista Nota de Credito/Debito de Gasto"
      backUrl="/user/expenses"
      primaryActions={[
        {
          onClick: () => {
            navigate(`/user/expenses/notes/add/${expenseId}`)
          },
          actionTitle: "Nuevo",
          color: "success"
        }
      ]}
    >
      <Container fluid>
        <Row>
          <Col xs="2" md="2">
            <h5><b>Provedor:</b></h5>
          </Col>
          <Col>
            <h5><b>{expense?.nombre}</b></h5>
          </Col>
        </Row>
        <Row>
          <Col xs="2" md="2">
            <b>Fecha:</b>
          </Col>
          <Col>
            {formatDate(expense?.fecha)}
          </Col>
        </Row>
        <Row>
          <Col xs="2" md="2">
            <b>Numero Factura:</b>
          </Col>
          <Col>
            {expense?.sequence}
          </Col>
        </Row>
        <Row>
          <Col xs="2" md="2">
            <b>Subtotal:</b>
          </Col>
          <Col>
            {formatMoneyValue(expense?.subtotal, expense?.currency?.currency_id)}
          </Col>
        </Row>
        <Row>
          <Col xs="2" md="2">
            <b>Descuento:</b>
          </Col>
          <Col>
            {formatMoneyValue(expense?.descuento, expense?.currency?.currency_id)}
          </Col>
        </Row>
        <Row>
          <Col xs="2" md="2">
            <b>Impuestos:</b>
          </Col>
          <Col>
            {formatMoneyValue(expense?.impuestos, expense?.currency?.currency_id)}
          </Col>
        </Row>
        <Row>
          <Col xs="2" md="2">
            <h5><b>Total:</b></h5>
          </Col>
          <Col>
            <h5><b>{formatMoneyValue(expense?.monto, expense?.currency?.currency_id)}</b></h5>
          </Col>
        </Row>
        <hr />
      </Container>
      <ModelIndex<ExpenseNoteDataType>
        ref={modelRef}
        fetchData={() => getInvoiceNotes()}
        primaryProperties={[
          {
            key: "sequence",
            highlighted: true,
            className: styles.sequence
          },
          {
            key: (d) => d.numero,
            alignment: "Start"
          },
        ]}
        primaryPropertiesSizing={
          [
            { sm: "4", xs: "12" },
            { sm: "8", xs: "12" }
          ]
        }
        secondaryProperties={[
          {
            key: (data) => {
              return formatDate(data.fecha)
            },
            title: "Fecha",
          },
          {
            key: (data) => {
              return PROVIDER_NOTE_TYPES[`${data.tipo}`]// ? 'Pagada' : 'Pendiente'
            },
          },
          {
            key: (data) => {
              return formatMoneyValue(data.total, data.currency.currency_id)
            },
            title: "Total",
            highlighted: true
          }
        ]}
        secondaryPropertiesSizing={
          [
            { sm: "3", md: "2" },
            { sm: "3", md: "2" },
            { sm: "3", md: "2" },
            { sm: "3", md: "2" }
          ]
        }
        listActions={[
          // {
          //   isVisible: (data) => !!data.atvMessages && data.atvMessages?.length > 0,
          //   href: "/user/atv-messages/listByInvoiceNote/{:id}",
          //   actionTitle: "Ver Mensajes de ATV",
          //   iconProps: { icon: faPaperPlane }
          // },
          {
            href: `/user/expenses/notes/edit/{:id}/${expenseId}`,
            actionTitle: "Editar Nota",
            iconProps: { icon: faEdit }
          },
          {
            actionTitle: "Eliminar Nota",
            iconProps: { icon: faTrash },
            needConfirm: true,
            confirmProps: {
              title: "Eliminar Nota",
              confirmMessage: "¿Seguro que desea eliminar esta Nota?",
              confirmBtnText: "Eliminar"
            },
            onClick: (id: string) => { deleteRecord(id) }
          }
        ]}
      >
      </ModelIndex>
      <LoadingScreen isLoading={isLoading} />
    </PageContainer >
  );
}

export default ExpenseNoteIndex;
